import React from "react";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { canAll } from "@src/security/permissions";

import styles from "./DiagnosticsDetails.module.styl";

const parseJsonIfPossible = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch (_e) {
    return value;
  }
};

const asPlainObject = (value) => {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
};

const getReportObject = (diagnosticOrReport) => {
  const value = parseJsonIfPossible(diagnosticOrReport?.report ?? diagnosticOrReport);
  return asPlainObject(value);
};

const getReportText = (diagnosticOrReport) => {
  const value = parseJsonIfPossible(diagnosticOrReport?.report ?? diagnosticOrReport);

  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object") {
    if (typeof value.text === "string") {
      return value.text;
    }

    if (typeof value.report === "string") {
      return value.report;
    }

    return JSON.stringify(value, null, 2);
  }

  return "";
};

const lineValue = (report, label) => {
  const text = getReportText(report);
  const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const equalsPattern = new RegExp(`^${escapedLabel}\\s*=\\s*(.*)$`, "mi");
  const equalsMatch = text.match(equalsPattern);
  if (equalsMatch) {
    return equalsMatch[1].trim();
  }

  const colonPattern = new RegExp(`^${escapedLabel}\\s*:\\s*(.*)$`, "mi");
  const colonMatch = text.match(colonPattern);
  return colonMatch ? colonMatch[1].trim() : "";
};

const firstMatch = (report, pattern) => {
  const match = getReportText(report).match(pattern);
  return match ? String(match[1] || "").trim() : "";
};

const generatedAt = (diagnostic) => getReportObject(diagnostic)?.generatedAt || lineValue(diagnostic, "generatedAt");

const eventType = (diagnostic) => getReportObject(diagnostic)?.eventType || getReportObject(diagnostic)?.type || "diagnostic";

const tabletId = (diagnostic) => getReportObject(diagnostic)?.deviceId || lineValue(diagnostic, "hardwareId");

const deviceModel = (diagnostic) => {
  const obj = getReportObject(diagnostic);

  if (obj?.deviceModel) {
    return obj.deviceModel;
  }

  return [lineValue(diagnostic, "manufacturer"), lineValue(diagnostic, "model")].filter(Boolean).join(" ");
};

const versionName = (diagnostic) => getReportObject(diagnostic)?.versionName || lineValue(diagnostic, "versionName");

const message = (diagnostic) => getReportObject(diagnostic)?.message || firstMatch(diagnostic, /^CAUSE\[0\]\s+message=(.*)$/mi) || lineValue(diagnostic, "message");

const eventClass = (diagnostic) => getReportObject(diagnostic)?.eventClass || firstMatch(diagnostic, /^CAUSE\[0\]\s+class=(.*)$/mi) || lineValue(diagnostic, "class");

const thread = (diagnostic) => lineValue(diagnostic, "thread");

const copyText = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(String(text || ""));
  }
};

const idFromLocation = () => {
  const path = String(window.location.pathname || "");
  const match = path.match(/\/diagnostics\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : "";
};

const buildSummary = (diagnostic) => {
  const obj = getReportObject(diagnostic);

  return {
    id: diagnostic?.id ?? "",
    generatedAt: generatedAt(diagnostic),
    eventType: eventType(diagnostic),
    tabletId: tabletId(diagnostic),
    deviceModel: deviceModel(diagnostic),
    versionName: versionName(diagnostic),
    message: message(diagnostic),
    eventClass: eventClass(diagnostic),
    thread: thread(diagnostic),
    localFileName: obj?.localFileName || "",
    format: obj?.format || lineValue(diagnostic, "format")
  };
};

const SummaryItem = ({ label, value }) => (
  <div className={styles.summaryItem}>
    <div className={styles.summaryLabel}>{label}</div>
    <div className={styles.summaryValue}>{value || "-"}</div>
  </div>
);

const DiagnosticsDetails = ({ gui }) => {
  const [diagnostic, setDiagnostic] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const id = idFromLocation();

  const load = React.useCallback(async () => {
    if (!id) {
      setLoading(false);
      setError(t("diagnostics.missingIdUrl"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const row = await rest.request(getServiceUri() + `diagnostics/${encodeURIComponent(id)}`, "GET");
      setDiagnostic(row || null);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    load();
  }, [load]);

  const report = getReportText(diagnostic);
  const summary = buildSummary(diagnostic);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {canAll(gui, ["READ_DIAGNOSTICS", "COUNT_DIAGNOSTICS"]) ? (
            <Tooltip title={t("diagnostics.back")}>
              <IconButton
                size="small"
                onClick={() => gui.goTo("/diagnostics")}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}

          <div>
            <div className={styles.title}>{t("diagnostics.details")}</div>
            <div className={styles.subtitle}>{id ? `${t("diagnostics.idPrefix")}: ${id}` : t("diagnostics.noId")}</div>
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={load}
            disabled={loading}
          >
            {t("reporting.refresh")}
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={() => copyText(report)}
            disabled={!report}
          >
            {t("diagnostics.copyReport")}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.notice}>{t("diagnostics.loading")}</div>
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : !diagnostic ? (
        <div className={styles.notice}>{t("diagnostics.notFound")}</div>
      ) : (
        <>
          <section className={styles.card}>
            <div className={styles.cardTitle}>{t("common.summary")}</div>

            <div className={styles.summaryGrid}>
              <SummaryItem label={t("common.generated")} value={summary.generatedAt} />
              <SummaryItem label={t("common.event")} value={summary.eventType} />
              <SummaryItem label={t("common.tabletId")} value={summary.tabletId} />
              <SummaryItem label={t("common.device")} value={summary.deviceModel} />
              <SummaryItem label={t("common.appVersion")} value={summary.versionName} />
              <SummaryItem label={t("common.message")} value={summary.message} />
              <SummaryItem label={t("common.class")} value={summary.eventClass} />
              <SummaryItem label={t("common.thread")} value={summary.thread} />
              <SummaryItem label={t("common.format")} value={summary.format} />
              <SummaryItem label={t("common.localFile")} value={summary.localFileName} />
            </div>
          </section>

          <section className={styles.card}>
            <div className={styles.cardTitle}>{t("common.report")}</div>
            <pre className={styles.report}>{report}</pre>
          </section>
        </>
      )}
    </div>
  );
};

export const displayDiagnosticsDetails = (gui) => <DiagnosticsDetails gui={gui} />;

export default displayDiagnosticsDetails;
